import { translate } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';

import { Constants } from 'app/constants/constants';
import { NumericMetadataField } from 'app/modules/widget/components/numeric-metadata-field-selector/numeric-metadata-field-selector.models';
import { Report } from 'app/providers/storage-api/report.service';
import { Utils } from 'app/utils/utils';
import {
  Comment,
  Comparison,
  Dashboard,
  Widget,
} from '../../../../../providers/bridge-api/dashboard.service';
import { TimeIntervalUtils } from '../../../../../utils/time-interval.utils';
import { WidgetChartOptions } from '../../../components/widget-chart/widget-chart.component';
import {
  getJourneyChannelLabel,
  resolveJourneyChannelIcon,
} from '../../../components/widget-journey/journey-channel.utils';
import {
  AbstractChartReport,
  AbstractDocumentsChartReport,
  AbstractFeatureChartReport,
} from '../chart-report-abstract/chart-report-abstract';
import { BigNumberDocumentsReport } from '../chart-report-bignumber/chart-report-bignumber-documents';
import { FeatureSentimentScoreOverTimeChartReport } from '../chart-report-feature-sentiment/chart-report-feature-sentiment';
import { ChartDataService } from '../../chart-data.service';
import { HighchartsOptions } from '../../chart-display.service';

export class DocumentsOverTimeChartReport extends AbstractDocumentsChartReport {
  reportName = 'documentsOverTime';
  reportHandler = ChartDataService.forDateHistogram;

  public isAnalyticItemSupported(_analyticItem: string): boolean {
    if (!this.options.widgetDataSource.hasDateData) {
      return false;
    }
    return true;
  }

  getParams() {
    const params = super.getParams();
    if (this.options.graphType === 'timeline') {
      params.includeContentChannel = true;
    }
    return params;
  }

  getFields() {
    return super.getFields().concat(['dateInterval']);
  }

  getAggregators() {
    return ['total', 'percentage', 'time', 'percentage_time'];
  }

  getGraphTypes() {
    return ['line', 'column', 'spline'];
  }

  public canSetChartGoal(): boolean {
    return true;
  }

  public canSetDateMarkers(): boolean {
    return true;
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const dateTextPart =
      widget.payload.dateInterval === 'week'
        ? 'widgets.charting.tooltip.date-part-week-bold'
        : 'widgets.charting.tooltip.date-part';
    ret.tooltip = {
      headerFormat: `${translate(dateTextPart, {
        date: "'{point.key:" + AbstractChartReport.DEFAULT_DATE_TIME_FORMAT + "'}",
      })}<br/>`,
      pointFormatter() {
        return `${translate('widgets.charting.tooltip.count-part', {
          count: this.documentCount ?? this.y,
        })}`;
      },
    };

    return ret;
  }
}

export class DocumentsSentimentPolarityChartReport extends AbstractDocumentsChartReport {
  reportName = 'documentSentimentPolarity';
  reportHandler = ChartDataService.forSentimentPolarity;

  isAnalyticItemSupported(_analyticItem) {
    return true;
  }

  getGraphTypes() {
    return ['column', 'bar', 'pie'];
  }

  getAggregators() {
    return ['total', 'percentage', 'time', 'percentage_time'];
  }

  canHideItems() {
    return false;
  }

  public canSetChartMaximum(): boolean {
    return this.options.aggregator !== 'percentage' && this.options.graphType !== 'pie';
  }

  public canShowAsStacked(): boolean {
    return !this.hasTimeInterval() && this.options.graphType !== 'pie';
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.enabled = false;
    if (widget.payload.graphType !== 'pie') {
      const sentimentScale = dashboard.payload.sentimentScale.toString();
      const tooltipTotals = this.getSentimentCountsForTooltips(response, sentimentScale);
      const formatSentimentTooltips = this.formatSentimentTooltips;
      ret.tooltip.formatter = function () {
        const polarity = this.series.name;
        const { count, percent } = formatSentimentTooltips(
          this.y,
          tooltipTotals,
          this.series,
          this.point,
          this.percentage,
          widget,
          this.point.pointId
        );

        return `${translate('widgets.charting.tooltip.polarity-part', { polarity })}<br>
        ${translate('widgets.charting.tooltip.count-part', {
          count,
        })}<br>
        ${translate('widgets.charting.tooltip.percentage-part', {
          percentage: percent.toFixed(0),
        })}`;
      };
    }

    return ret;
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }
}

export class DocumentsPowerScoreChartReport extends BigNumberDocumentsReport {
  reportName = 'documentPowerScorePolarity';
}

export class DocumentsSentimentPolarityOverTimeChartReport extends AbstractDocumentsChartReport {
  reportName = 'documentSentimentPolarityOverTime';
  reportHandler = ChartDataService.forSentimentPolarityOverTime;

  isAnalyticItemSupported(analyticItem) {
    return ['metadata'].indexOf(analyticItem) < 0;
  }

  getFields() {
    return super.getFields().concat(['dateInterval']);
  }

  getGraphTypes() {
    return ['line', 'column', 'spline'];
  }

  getAggregators() {
    return ['total', 'percentage', 'time', 'percentage_time'];
  }

  public canSetChartMaximum(): boolean {
    return this.options.aggregator !== 'percentage';
  }

  public canSetDateMarkers(): boolean {
    return true;
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }
}

export class DocumentsSentimentScoreOverTimeReport extends AbstractDocumentsChartReport {
  reportName = 'documentSentimentScoreOverTime';
  reportHandler = ChartDataService.forDateHistogram;

  isAnalyticItemSupported(analyticItem) {
    return ['metadata'].indexOf(analyticItem) < 0;
  }

  getFields() {
    return super.getFields().concat(['dateInterval']);
  }

  getAggregators() {
    return ['AVG', 'SUM'];
  }

  getGraphTypes() {
    return ['line', 'column', 'spline'];
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    const aggregatorSuffix = widget.payload.aggregator.includes('AVG') ? 'Avg' : 'Sum';

    ret.tooltip.pointFormatter = function () {
      return translate('widgets.charting.tooltip.sentiment-aggregator-part', {
        aggregatorSuffix,
        sentiment: Utils.round(this.y, this.series.options.maxDecimals),
      });
    };

    return ret;
  }
}

export class FeaturePowerScoreOverTimeChartReport extends FeatureSentimentScoreOverTimeChartReport {
  reportName = 'featurePowerScoreOverTime';
}

export class DocumentsPowerScoreOverTimeReport extends DocumentsSentimentScoreOverTimeReport {
  reportName = 'documentPowerScoreOverTime';
}

export class DocumentTopFeaturesChartReport extends AbstractFeatureChartReport {
  reportName = 'documentTopFeatures';

  reportHandler = (dashboard, widget, response, options) =>
    ChartDataService.forDocumentCount(dashboard, widget, response, options, this.options);

  isAnalyticItemSupported(_key: string) {
    return true;
  }

  getGraphTypes() {
    return ['bar', 'column', 'pie'];
  }

  getAggregators() {
    return ['total', 'percentage', 'time', 'percentage_time'];
  }

  canDrilldownMentions() {
    return true;
  }

  canDrilldownThemes() {
    return true;
  }

  canSort() {
    return this.options.graphType !== 'pie';
  }

  public canSetChartMaximum(): boolean {
    return this.options.graphType !== 'pie';
  }

  public canSetChartGoal(): boolean {
    return this.options.graphType !== 'pie';
  }

  canDisplayChartValues(): boolean {
    return true;
  }

  canColorByPoint(): boolean {
    return true;
  }

  isDisplayChartValuesDisabled(): boolean {
    return this.options.graphType === 'pie' || this.options.aggregator === 'time';
  }

  isColorByPointDisabled(): boolean {
    return this.options.graphType === 'pie';
  }

  displayChartValuesDisabledTooltip(): string {
    return translate('chart-report.pie-ot-display-values');
  }

  colorByPointDisabledTooltip(): string {
    return translate('chart-report.pie-ot-color-by-point');
  }

  tooltipValueRowFn(aggregator) {
    const documentCount = (seriesIdx) => {
      if (this.options.comparisonBucketsCounts) {
        return this.options.comparisonBucketsCounts[seriesIdx];
      }

      if (seriesIdx) {
        return this.options.currentPeriodDocumentCount;
      }
      return this.options.previousPeriodDocumentCount ?? this.options.filteredDocumentCount;
    };

    return (pointDocumentCount, seriesIdx) => {
      const docCount = documentCount(seriesIdx);
      const countPart = `${Utils.addThousandSeparator(
        pointDocumentCount
      )} / ${Utils.addThousandSeparator(docCount)}`;
      const percentagePart = Utils.roundNearest((pointDocumentCount * 100) / docCount);

      if (aggregator === 'percentage') {
        return translate('widgets.charting.tooltip.percentage-count-part', {
          percentage: percentagePart,
          count: countPart,
        });
      }

      return translate('widgets.charting.tooltip.count-percentage-part', {
        count: countPart,
        percentage: percentagePart,
      });
    };
  }

  getHighchartsOptions(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    if (widget.payload.type === 'documentCount') {
      const valueRowFormatter = this.tooltipValueRowFn(widget.payload.aggregator);

      ret.tooltip = {
        formatter() {
          const name = this.point.pointId;
          const valueRow = valueRowFormatter(this.point.documentCount, this.series.index);

          return `${translate('widgets.charting.tooltip.name-part', { name })}<br>
          ${valueRow}`;
        },
      };
    }

    return ret;
  }
}

export class DocumentsChartReport extends DocumentTopFeaturesChartReport {
  reportName = 'documentsCount';

  reportHandler = (dashboard, widget, response, options) =>
    ChartDataService.forDocumentCount(dashboard, widget, response, options, this.options);

  getHighchartsOptions(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: HighchartsOptions
  ) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    ret.xAxis.labels = {
      formatter() {
        return this.value.slice(0, 1).toUpperCase() + this.value.slice(1);
      },
    };

    return ret;
  }

  canDrilldownMentions() {
    return false;
  }

  canDrilldownThemes() {
    return false;
  }

  public canHideItems() {
    return false;
  }
}
export class MetadataTopFeaturesChartReport extends DocumentTopFeaturesChartReport {
  reportName = 'metadataTopFeatures';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const nameFormatterFn = this.formatMetadataValueFactory;
    const valueRowFormatter = this.tooltipValueRowFn(widget.payload.aggregator);

    ret.tooltip = ret.tooltip || {};
    ret.tooltip.formatter = function () {
      const valueRow = valueRowFormatter(this.point.documentCount, this.series.index);

      return `${translate('widgets.charting.tooltip.name-part', {
        name: nameFormatterFn(this.point.pointId || this.key),
      })}<br>
        ${valueRow}`;
    };

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      return nameFormatterFn(this.value);
    };

    if (this.options.graphType === 'pie') {
      ret.series[0].dataLabels = ret.series[0].dataLabels || {};
      ret.series[0].dataLabels.formatter = function () {
        return nameFormatterFn(this.key);
      };
    }

    return ret;
  }
}

export class TaxonomyTopFeaturesChartReport extends DocumentTopFeaturesChartReport {
  reportName = 'taxonomyTopFeatures';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getTaxonomyHighChartsOptions(dashboard, widget, response, options);
    const taxonomyNodeFromIdFn = super.getTaxonomyNodeFromIdFn(ret);
    const valueRowFormatter = this.tooltipValueRowFn(widget.payload.aggregator);

    ret.tooltip = ret.tooltip || {};
    ret.tooltip.formatter = function () {
      const valueRow = valueRowFormatter(this.point.documentCount, this.series.index);

      const id = this.point.pointId || this.key;
      const taxonomyNode = taxonomyNodeFromIdFn(id);

      return `${translate('widgets.charting.tooltip.name-part', { name: taxonomyNode.name })}<br>
            ${translate('widgets.charting.tooltip.path-part', { path: taxonomyNode.path })}<br>
            ${valueRow}`;
    };

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      const labelId = this.value || this.key;
      const taxonomyNode = taxonomyNodeFromIdFn(labelId);
      return taxonomyNode?.name || labelId;
    };

    return ret;
  }
}

export class DocumentTopFeaturesOverTimeChartReport extends AbstractFeatureChartReport {
  reportName = 'documentTopFeaturesOverTime';
  reportHandler = (dashboard, widget, response, options) =>
    ChartDataService.forDateHistogram(dashboard, widget, response, options, this.options);

  isAnalyticItemSupported(_key: string) {
    return true;
  }

  getFields() {
    return super.getFields().concat(['dateInterval']);
  }

  getGraphTypes() {
    return ['line', 'column', 'spline'];
  }

  getAggregators() {
    return ['total', 'percentage', 'time', 'percentage_time'];
  }

  public canSetDateMarkers(): boolean {
    return true;
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    if (widget.payload.type === 'documentCount') {
      const dateTextPart =
        widget.payload.dateInterval === 'week'
          ? 'widgets.charting.tooltip.date-part-week-bold'
          : 'widgets.charting.tooltip.date-part';
      ret.tooltip = {
        headerFormat: `${translate(dateTextPart, {
          date: "'{point.key:" + AbstractChartReport.DEFAULT_DATE_TIME_FORMAT + "'}",
        })}<br/>`,
        pointFormatter() {
          let label;
          if (widget.payload.analyticItem === 'documents') {
            label = '';
          } else if (widget.payload.aggregator === 'percentage_time') {
            label = translate('widgets.charting.tooltip.percentage-count-total-part', {
              percentage: this.y,
              count: this.documentCount,
              total: this.totalDocumentCountByDate,
            });
          } else {
            label = translate('widgets.charting.tooltip.count-part', {
              count: Utils.addThousandSeparator(this.y),
            });
          }

          return `${translate('widgets.charting.tooltip.name-part', {
            name: this.series.name,
          })} <span style="color: ${
            this.color
          }">${DocumentTopFeaturesOverTimeChartReport.getSeriesSymbolChar(
            this.series.symbol
          )}</span><br/>
            ${label}
          `;
        },
      };
    }
    return ret;
  }
}

export class DocumentTopMetadataOverTimeChartReport extends DocumentTopFeaturesOverTimeChartReport {
  reportName = 'documentTopMetadataOverTime';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);
    const formatterFn = this.formatMetadataValueFactory;

    ret.series.forEach((s) => {
      s.custom = {
        seriesId: s.name,
      };
      s.name = formatterFn(s.name);
    });

    return ret;
  }
}

export class TaxonomyTopFeaturesOverTimeChartReport extends DocumentTopFeaturesOverTimeChartReport {
  reportName = 'taxonomyTopFeaturesOverTime';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getTaxonomyHighChartsOptions(dashboard, widget, response, options);

    const taxonomyNodeFromIdFn = super.getTaxonomyNodeFromIdFn(ret);

    ret.series.forEach((s) => {
      s.custom = {
        seriesId: s.data[0].pointId,
      };
    });

    ret.tooltip = ret.tooltip || {};
    ret.tooltip.formatter = function () {
      const index = this.series.data.indexOf(this.point);
      const seriesData = this.series.data[index];
      const taxonomyNode = taxonomyNodeFromIdFn(seriesData.pointId);
      const dateTextPart =
        widget.payload.dateInterval === 'week'
          ? 'widgets.charting.tooltip.date-part-week-bold'
          : 'widgets.charting.tooltip.date-part';

      const datePrefix = `${translate(dateTextPart, {
        date: Highcharts.dateFormat(this.DEFAULT_DATE_FORMAT, +this.x),
      })}<br/>`;
      const nameSuffix = `<span style="color: ${this.color}">
      ${TaxonomyTopFeaturesOverTimeChartReport.getSeriesSymbolChar(this.series.symbol)}</span>`;

      let label;
      if (widget.payload.aggregator === 'percentage_time') {
        label = translate('widgets.charting.tooltip.percentage-count-total-part', {
          percentage: this.y,
          count: seriesData.documentCount,
          total: seriesData.totalDocumentCountByDate,
        });
      } else {
        label = translate('widgets.charting.tooltip.count-part', {
          count: Utils.addThousandSeparator(this.y),
        });
      }

      return (
        datePrefix +
        `${translate('widgets.charting.tooltip.name-part', {
          name: taxonomyNode.name + ' ' + nameSuffix,
        })}<br>
            ${translate('widgets.charting.tooltip.path-part', { path: taxonomyNode.path })}<br>
            ${label}<br>`
      );
    };

    return ret;
  }
}

export class DocumentNumericMetadataOverTimeChartReport extends AbstractFeatureChartReport {
  reportName = 'documentNumericMetadataOverTime';

  reportHandler = ChartDataService.forNumericMetadataOverTime;

  isAnalyticItemSupported(analyticItem) {
    return ['metadata'].indexOf(analyticItem) > -1;
  }

  getGraphTypes() {
    return ['line', 'spline'];
  }

  getAggregators(): string[] {
    return [];
  }

  hasTimeInterval(): boolean {
    return true;
  }

  getFields() {
    return super.getFields().concat(['dateInterval']);
  }

  getOptionalFields() {
    return [];
  }

  public canHideItems(): boolean {
    return false;
  }

  public canDrilldown(): boolean {
    return false;
  }

  public canSort(): boolean {
    return false;
  }

  public canSetChartGoal(): boolean {
    return true;
  }

  public canSetDateMarkers(): boolean {
    return true;
  }

  public canSetChartMaximumPercentage(): boolean {
    return this.options.type !== 'journey';
  }

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    ret.tooltip.pointFormatter = undefined;
    const dateInterval = widget.payload.expandedJourneyDateInterval ?? widget.payload.dateInterval;
    const timezone = dashboard.payload.timezone ?? 'UTC';

    ret.tooltip = {
      ...ret.tooltip,
      headerFormat: '',
      valueDecimals: 2,
      pointFormatter: function (_point) {
        const formattedDate = TimeIntervalUtils.formatTimeInterval(
          String(this.x),
          dateInterval,
          timezone,
          '',
          '',
          false
        );
        const tooltipHeader = `${translate('widgets.charting.tooltip.date-part-interval', {
          interval: translate(`widgets.charting.tooltip.interval-label.${dateInterval}`),
          date: formattedDate,
        })}<br/>`;

        return `${tooltipHeader}${translate('widgets.charting.tooltip.name-part', {
          name: this.series.name,
        })} <span style="color: ${this.color}">\u25CF</span> ${Utils.addThousandSeparator(
          Utils.round(this.y, this.series.options.maxDecimals)
        )}<br>${translate('widgets.charting.tooltip.document-count-part', {
          count: Utils.addThousandSeparator(Utils.round(this.options.documentCount)),
        })}`;
      },
    };

    const setChartMaximum = (isPercentage: boolean, max?: number) => {
      if (typeof max !== 'number') {
        return;
      }

      const axis = options.yAxis.find((a) => a.isPercentage === isPercentage);
      if (!axis) {
        return;
      }

      this.setChartAxisValues(undefined, max, undefined, axis);
    };

    setChartMaximum(false, widget.payload.chartMaximum);

    setChartMaximum(true, widget.payload.chartMaximumPercentage);

    if ((widget.payload as any).journeyTimelineRole === 'nmot') {
      ret.xAxis = {
        ...ret.xAxis,
        visible: true,
        lineColor: 'transparent',
        tickColor: 'transparent',
        labels: { enabled: false },
      };
      ret.legend = { ...ret.legend, enabled: false };
      const existingSpline = ret.plotOptions?.spline;
      ret.plotOptions = {
        ...ret.plotOptions,
        spline: {
          ...existingSpline,
          marker: {
            ...(existingSpline as Highcharts.PlotSplineOptions | undefined)?.marker,
            enabled: true,
            radius: 3,
          },
        },
      };
    }

    if (widget.payload.graphType === 'timeline') {
      ret.chart = {
        ...ret.chart,
        type: 'timeline',
        zoomType: false,
        backgroundColor: Constants.JOURNEY_TIMELINE_BG,
        plotBackgroundColor: Constants.JOURNEY_TIMELINE_BG,
        spacingTop: 20,
      };
      ret.xAxis = {
        ...ret.xAxis,
        visible: true,
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          ...(ret.xAxis as any)?.labels,
          y: 32,
        },
        plotLines: [], // Remove date markers from timeline view
      };
      ret.yAxis = { visible: false };
      ret.legend = { ...ret.legend, enabled: false };
      ret.plotOptions = {
        ...ret.plotOptions,
        timeline: {
          marker: {
            enabled: false,
            states: {
              hover: { enabled: false },
            },
          },
          dataLabels: {
            enabled: true,
            useHTML: true,
            allowOverlap: true,
            distance: 0,
            borderWidth: 0,
            borderColor: 'transparent',
            backgroundColor: 'transparent',
            shadow: false,
            padding: 0,
            style: {
              color: '#fff',
              fontSize: '12px',
              fontWeight: 'bold',
              textOutline: 'none',
            },
            formatter() {
              const displayCount =
                this.point.options.clusterSize ?? this.point.options.documentCount;
              if (this.point.options.isCluster || displayCount > 1) {
                return `<span style="
                  font-size: 14px;
                  font-weight: bold;
                  background-color: #253746;
                  color: #fff;
                  border-radius: 50%;
                  width: 36px;
                  height: 36px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                  border: 4px solid ${Constants.JOURNEY_TIMELINE_BG};
                ">${displayCount}</span>`;
              }
              const parentMap = widget.payload.contentChannelParentMap ?? {};
              const labelMap = widget.payload.contentChannelLabelMap ?? {};
              const channels = this.point.options.contentChannels ?? [];
              const channelId = channels[0]?.id;
              const channelLabel = getJourneyChannelLabel(channelId, labelMap, parentMap);
              const iconClass = resolveJourneyChannelIcon(channelLabel);
              return `<span class="kxi-icon ${iconClass}" style="
              font-size: 20px;
              background-color: #253746;
              color: #fff;
              border-radius: 50%;
              width: 36px;
              height: 36px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              border: 4px solid ${Constants.JOURNEY_TIMELINE_BG};
              "></span>`;
            },
          },
          lineWidth: 4,
          lineColor: '#253746',
        },
      };
      ret.tooltip = {
        ...ret.tooltip,
        useHTML: true,
        formatter: function () {
          // Aggregated markers (a drillable journey bucket or a proximity cluster) have no single
          // touchpoint to describe, so no tooltip is shown for them.
          const pointCount = this.point.options.clusterSize ?? this.point.options.documentCount;
          if (pointCount > 1) {
            return false;
          }
          const date = Highcharts.dateFormat(this.point.DEFAULT_DATE_FORMAT, this.point.x);
          const parentMap = widget.payload.contentChannelParentMap ?? {};
          const labelMap = widget.payload.contentChannelLabelMap ?? {};
          const seen = new Set<string>();
          const channels = (this.point.options.contentChannels ?? []).filter(
            (ch) => ch.id && !seen.has(ch.id) && seen.add(ch.id)
          );
          const channelPaths = channels.map((ch) => {
            const path: string[] = [];
            let currentId = ch.id;
            while (currentId && labelMap[currentId]) {
              if (parentMap[currentId]) {
                path.unshift(labelMap[currentId]);
              }
              currentId = parentMap[currentId];
            }
            return path.join(' / ');
          });
          const channelLine = channelPaths.length
            ? `<br><b>${translate('widgets.journey.content-channel')}:</b> ${channelPaths.join('<br>')}`
            : '';
          return `<b>${translate('widgets.charting.tooltip.date-part')}</b> ${date}${channelLine}<br>${translate(
            'widgets.charting.tooltip.document-count-part',
            { count: Utils.addThousandSeparator(Utils.round(this.point.options.documentCount)) }
          )}`;
        },
      };
    }

    return ret;
  }

  public applyComparisonToSeries(comparison: any, series: any, seriesIdx: any): void {
    if (this.options.numericMetadataFields?.length === 1) {
      super.applyComparisonToSeries(comparison, series, seriesIdx);
    }
  }

  isCommentVisible(comment: Comment, widget: Widget, series) {
    if (!super.isCommentVisible(comment, widget, series)) {
      return false;
    }

    return comment.payload.dateInterval === widget.payload.dateInterval;
  }

  getCommentsForSeries(
    series: any,
    widgetPayload: WidgetChartOptions,
    comparison?: Comparison
  ): Comment[] {
    return super
      .getCommentsForSeries(series, widgetPayload, comparison)
      .filter((c) => series.data.some((d) => d.x === c.target.point.x));
  }

  private getNumericParams(numericMetadataField: NumericMetadataField) {
    const params: any = {
      customField: numericMetadataField.fieldId,
    };

    switch (numericMetadataField.scoring) {
      default:
      case 'avg':
        params.scoringStrategy = 'AVERAGE';
        break;
      case 'sum':
        params.scoringStrategy = params.customField === 'docs' ? 'COUNT' : 'SUM';
        break;
      case 'top':
      case 'bottom':
        params.scoringStrategy = 'BOX';
        params.boxDirection = numericMetadataField.scoring === 'top' ? 'TOP' : 'BOTTOM';
        params.boxSize = numericMetadataField.boxSize;
        break;
      case 'nps':
        params.scoringStrategy = 'NPS';
        break;
      case 'LAST':
        params.scoringStrategy = 'LAST';
        break;
    }

    if (numericMetadataField.min && numericMetadataField.max) {
      params.scoreRange = `${numericMetadataField.min}:${numericMetadataField.max}`;
    }

    return params;
  }

  public async executeReport(format: 'json' | 'csv', passedQuery: string): Promise<any> {
    const params = super.getParams();
    // Support subject filtering for journey drilldown
    if (this.options.subject) {
      params.subject = this.options.subject;
    }
    if (this.options.subjectValue) {
      params.subjectValue = this.options.subjectValue;
      params.includeContentChannel = true;
    }
    const body = this.options.numericMetadataFields.map((f) => this.getNumericParams(f));

    const response = await this.reportService.postReport(
      this.options.widgetDataSource.collectionName,
      this.options.widgetDataSource.groupId,
      this.reportName,
      params,
      body,
      format,
      passedQuery,
      this.filterService.buildFilterStringClearPolarityFilters(
        this.options.filters,
        this.options.analyticItem
      ),
      undefined,
      this.options.projectId
    );

    if (format === 'csv') {
      return this.processCsvResponse(response);
    } else {
      this.lastDocumentCount = (<Report>response).totalHits;
    }
    return response;
  }

  private processCsvResponse(response) {
    let body = response.split('\n');
    const header = body.splice(0, 1);

    for (const metadataField of this.options.numericMetadataFields) {
      const fieldId = metadataField.fieldId;
      const fieldLabel = metadataField.label ?? fieldId;

      body = body.map((row) => row.replace(`,${fieldId},`, `,${fieldLabel},`));
    }
    body.sort().reverse();

    return `${header}\n${body.join('\n')}`;
  }
}

export class DocumentTopFeaturesSentimentPolarityChartReport extends AbstractFeatureChartReport {
  reportName = 'documentSentimentPolarityTopFeatures';
  reportHandler = ChartDataService.forSentimentPolarity;

  isAnalyticItemSupported(_analyticItem) {
    return true;
  }

  getOptionalFields(): string[] {
    return ['excludedValues'];
  }

  getGraphTypes() {
    return ['column', 'bar'];
  }

  getAggregators() {
    return ['total', 'percentage'];
  }

  public canSetChartMaximum(): boolean {
    return this.options.aggregator !== 'percentage';
  }

  public canShowAsStacked(): boolean {
    return true;
  }

  public applyComparisonToSeries(_comparison: any, _series: any, _seriesIdx: any): void {
    return;
  }
}

export class DocumentTopMetadataSentimentPolarityChartReport extends DocumentTopFeaturesSentimentPolarityChartReport {
  reportName = 'documentSentimentPolarityTopMetadata';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getHighchartsOptions(dashboard, widget, response, options);

    const formatterFn = this.formatMetadataValueFactory;
    const sentimentScale = dashboard.payload.sentimentScale.toString();
    const tooltipTotals = this.getSentimentCountsForTooltips(response, sentimentScale);
    const formatSentimentTooltips = this.formatSentimentTooltips;

    ret.tooltip = ret.tooltip || {};
    ret.tooltip.formatter = function () {
      const { count, percent } = formatSentimentTooltips(
        this.y,
        tooltipTotals,
        this.series,
        this.point,
        this.percentage,
        widget,
        formatterFn(this.point.pointId)
      );
      const polarity = this.series.name;
      return `${translate('widgets.charting.tooltip.name-part', {
        name: formatterFn(this.point.pointId),
      })}<br>
        ${translate('widgets.charting.tooltip.polarity-part', { polarity })}<br>
        ${translate('widgets.charting.tooltip.count-part', {
          count,
        })}<br>
        ${translate('widgets.charting.tooltip.percentage-part', {
          percentage: percent.toFixed(0),
        })}`;
    };

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      return formatterFn(this.value);
    };

    return ret;
  }
}

export class TaxonomySentimentPolarityChartReport extends DocumentTopFeaturesSentimentPolarityChartReport {
  reportName = 'taxonomySentimentPolarity';

  getHighchartsOptions(dashboard, widget, response, options) {
    const ret = super.getTaxonomyHighChartsOptions(dashboard, widget, response, options);

    const taxonomyNodeFromIdFn = super.getTaxonomyNodeFromIdFn(ret);
    const sentimentScale = dashboard.payload.sentimentScale.toString();
    const tooltipTotals = this.getSentimentCountsForTooltips(response, sentimentScale);
    const formatSentimentTooltips = this.formatSentimentTooltips;

    ret.tooltip = ret.tooltip || {};
    ret.tooltip.formatter = function () {
      const id = this.point.pointId || this.key;
      const { count, percent } = formatSentimentTooltips(
        this.y,
        tooltipTotals,
        this.series,
        this.point,
        this.percentage,
        widget,
        id
      );
      const taxonomyNode = taxonomyNodeFromIdFn(id);
      const { aggregator, showAsStacked } = widget.payload;

      if (aggregator === 'percentage' && showAsStacked && this.series.name === 'Total volume') {
        return `${translate('widgets.charting.tooltip.name-part', { name: taxonomyNode.name })}<br>
          ${translate('widgets.charting.tooltip.path-part', { path: taxonomyNode.path })}<br>
           ${translate('widgets.charting.tooltip.count-part', {
             count,
           })}
        `;
      }

      return `${translate('widgets.charting.tooltip.name-part', { name: taxonomyNode.name })}<br>
          ${translate('widgets.charting.tooltip.path-part', { path: taxonomyNode.path })}<br>
          ${translate('widgets.charting.tooltip.polarity-part', { polarity: this.series.name })}<br>
          ${translate('widgets.charting.tooltip.count-part', {
            count,
          })}<br>
          ${translate('widgets.charting.tooltip.percentage-part', {
            percentage: percent.toFixed(0),
          })}`;
    };

    ret.xAxis.labels = ret.xAxis.labels || {};
    ret.xAxis.labels.formatter = function () {
      const taxonomyNode = taxonomyNodeFromIdFn(this.value);
      return taxonomyNode?.name || this.value;
    };

    return ret;
  }
}
